import React, { useEffect, useState } from 'react';
import { Save, Truck } from 'lucide-react';
import { ChromePicker } from 'react-color';
import { neo4jService } from '../../services/neo4jService';
import { normalizeFreeShippingCard } from '../../config/freeShippingCard';
import './ManageFreeShippingCard.css';

const ManageFreeShippingCard = () => {
  const [settings, setSettings] = useState(normalizeFreeShippingCard());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const loadedSettings = await neo4jService.getFreeShippingCard();
        setSettings(normalizeFreeShippingCard(loadedSettings));
        setError('');
      } catch (err) {
        setError('Failed to load free shipping card settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setSuccess('');
    setError('');
  };

  const handleColorChange = (color) => {
    setSettings(prev => ({
      ...prev,
      color: color.hex
    }));
    setSuccess('');
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const savedSettings = await neo4jService.updateFreeShippingCard(settings);
      setSettings(normalizeFreeShippingCard(savedSettings));
      setSuccess('Homepage shipping card updated successfully');
      setError('');
    } catch (err) {
      setError('Failed to save homepage shipping card settings');
      setSuccess('');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="manage-free-shipping-card">
      <h2>Homepage Shipping Card</h2>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="shipping-settings-layout">
        <form className="shipping-settings-form" onSubmit={handleSubmit}>
          <label className="toggle-row">
            <input
              type="checkbox"
              name="enabled"
              checked={settings.enabled}
              onChange={handleChange}
            />
            Show shipping card on homepage
          </label>

          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              value={settings.title}
              onChange={handleChange}
              placeholder="Free Shipping"
            />
          </div>

          <div className="form-group">
            <label>Homepage Text</label>
            <input
              type="text"
              name="homeDescription"
              value={settings.homeDescription}
              onChange={handleChange}
              placeholder="For orders from $50"
            />
          </div>

          <div className="form-group color-picker-group">
            <label>Accent Color</label>
            <button
              type="button"
              className="color-preview"
              onClick={() => setShowColorPicker(prev => !prev)}
            >
              <span className="color-swatch" style={{ backgroundColor: settings.color }} />
              <span>{settings.color}</span>
            </button>
            {showColorPicker && (
              <div className="color-picker-popover">
                <div className="color-picker-cover" onClick={() => setShowColorPicker(false)} />
                <ChromePicker
                  color={settings.color}
                  onChange={handleColorChange}
                  disableAlpha
                />
              </div>
            )}
          </div>

          <button type="submit" className="save-shipping-button" disabled={saving}>
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Card'}
          </button>
        </form>

        <div className="shipping-preview-panel">
          <h3>Preview</h3>
          <div className={`shipping-preview-card ${!settings.enabled ? 'disabled' : ''}`}>
            <Truck size={32} style={{ color: settings.color }} />
            <div>
              <h4>{settings.title || 'Shipping hidden when blank'}</h4>
              <p>{settings.homeDescription || 'No homepage shipping text'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageFreeShippingCard;
